// src/components/games/StarRatingReadOnly.js
import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../../config';

function Star({ filled, partial = 0 }) {
  // Partial fill für halbe Sterne (0-1)
  const fillId = `star-fill-${Math.random().toString(36).substr(2, 9)}`;
  
  return (
    <svg
      viewBox="0 0 16 16"
      width="14"
      height="14"
      aria-hidden="true"
      style={{ color: filled || partial > 0 ? '#ffcf40' : '#666' }}
    >
      {partial > 0 && (
        <defs>
          <linearGradient id={fillId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset={`${partial * 100}%`} stopColor="#ffcf40" />
            <stop offset={`${partial * 100}%`} stopColor="#666" />
          </linearGradient>
        </defs>
      )}
      <path 
        d="M8 1.5l2.1 4.2 4.7.7-3.4 3.3.8 4.7L8 12.2 3.8 14.4l.8-4.7L1.2 6.4l4.7-.7L8 1.5z" 
        fill={filled ? 'currentColor' : partial > 0 ? `url(#${fillId})` : 'none'}
        stroke={filled || partial > 0 ? 'none' : 'currentColor'}
        strokeWidth={filled || partial > 0 ? '0' : '1.6'}
      />
    </svg>
  );
}

export default function StarRatingReadOnly({ gameKey }) {
  const [data, setData] = useState({
    average: 0,
    count: 0,
    userRating: 0,
    hasRated: false,
    loading: true,
    error: null
  });

  useEffect(() => {
    let cancelled = false;
    
    const fetchRating = async () => {
      try {
        setData(prev => ({ ...prev, loading: true, error: null }));
        
        const headers = {};
        const token = 
          localStorage.getItem('token') ||
          localStorage.getItem('authToken') ||
          localStorage.getItem('jwt') ||
          localStorage.getItem('accessToken');
          
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        // Client-ID für anonyme Nutzer - use same key as StarRating
        const clientId = localStorage.getItem('va:clientId') || 
          localStorage.getItem('clientId') || 
          `client-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        if (!localStorage.getItem('va:clientId')) {
          localStorage.setItem('va:clientId', clientId);
        }
        headers['x-client-id'] = clientId;

        const url = `${API_BASE_URL}/game-ratings/${encodeURIComponent(gameKey)}`;
        const response = await fetch(url, { headers });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const result = await response.json();
        
        if (!cancelled) {
          setData({
            average: Number(result.average) || 0,
            count: Number(result.count) || 0,
            userRating: Number(result.userRating) || 0,
            hasRated: Boolean(result.hasRated),
            loading: false,
            error: null
          });
        }
      } catch (error) {
        console.warn(`Failed to fetch rating for ${gameKey}:`, error);
        
        if (!cancelled) {
          // Fallback: Lokale Daten wenn verfügbar
          const localRating = Number(localStorage.getItem(`vaRating:${gameKey}`) || 0);
          setData({
            average: localRating,
            count: localRating > 0 ? 1 : 0,
            userRating: localRating,
            hasRated: localRating > 0,
            loading: false,
            error: error.message
          });
        }
      }
    };

    if (gameKey) {
      fetchRating();
    }

    // Listen for rating changes from other components
    const handleRatingChange = (event) => {
      if (event.detail.gameKey === gameKey) {
        console.log(`[RATINGS] ReadOnly received update for ${gameKey}:`, event.detail);
        setData(prevData => ({
          ...prevData,
          average: event.detail.average,
          count: event.detail.count,
          userRating: event.detail.userRating,
          hasRated: event.detail.userRating > 0,
          loading: false,
          error: null
        }));
      }
    };

    window.addEventListener('ratingChanged', handleRatingChange);

    return () => {
      cancelled = true;
      window.removeEventListener('ratingChanged', handleRatingChange);
    };
  }, [gameKey]);

  if (data.loading) {
    return (
      <div 
        className="star-rating-loading"
        style={{ 
          display: 'inline-flex', 
          gap: 2, 
          color: '#666',
          alignItems: 'center',
          minWidth: '80px'
        }}
      >
        {[1, 2, 3, 4, 5].map((n) => (
          <Star key={n} filled={false} />
        ))}
      </div>
    );
  }

  const { average, count } = data;
  const fullStars = Math.floor(average);
  const partialStar = average - fullStars;
  
  const title = count > 0 
    ? `${average.toFixed(1)}/5 stars (${count} rating${count !== 1 ? 's' : ''})`
    : 'No ratings yet';

  return (
    <div
      className="star-rating-readonly"
      title={title}
      style={{ 
        display: 'inline-flex', 
        gap: 2, 
        alignItems: 'center',
        minWidth: '80px'
      }}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        if (n <= fullStars) {
          return <Star key={n} filled={true} />;
        } else if (n === fullStars + 1 && partialStar > 0.1) {
          return <Star key={n} filled={false} partial={partialStar} />;
        } else {
          return <Star key={n} filled={false} />;
        }
      })}
      {count > 0 && (
        <span 
          className="rating-text"
          style={{ 
            fontSize: '11px', 
            color: '#999', 
            marginLeft: '4px',
            fontWeight: '500'
          }}
        >
          ({count})
        </span>
      )}
    </div>
  );
}